import axios from "axios";
import { getOcmKey, getOcmPriority } from "./settings";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const http = axios.create({ baseURL: API, timeout: 35000 });

function extractError(e) {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return e?.message || "Une erreur est survenue";
}

export async function geocode(text, size = 6) {
  try {
    const { data } = await http.get(`/geocode`, { params: { text, size } });
    return data.results || [];
  } catch (e) {
    throw new Error(extractError(e));
  }
}

export async function autocomplete(text, focus = null, size = 6) {
  try {
    const params = { text, size };
    if (focus && focus.lon != null && focus.lat != null) {
      params.focus_lon = focus.lon;
      params.focus_lat = focus.lat;
    }
    const { data } = await http.get(`/autocomplete`, { params });
    return data.results || [];
  } catch (e) {
    // Fallback to standard geocoding if autocomplete fails
    return geocode(text, size);
  }
}

export async function reverseGeocode(lon, lat) {
  const { data } = await http.get(`/reverse`, { params: { lon, lat } });
  return data;
}

export async function computeRoute({ start, end, via = null, stops = null, avoid_highways = true, avoid_tollways = false, alternatives = 1, extra_avoid = null }) {
  try {
    const { data } = await http.post(`/route`, { start, end, via, stops, avoid_highways, avoid_tollways, alternatives, extra_avoid });
    return data;
  } catch (e) {
    throw new Error(extractError(e));
  }
}

export async function createShare(payload) {
  try {
    const { data } = await http.post(`/share`, payload);
    return data;
  } catch (e) {
    throw new Error(extractError(e));
  }
}

export async function getShare(id) {
  try {
    const { data } = await http.get(`/share/${id}`);
    return data;
  } catch (e) {
    throw new Error(extractError(e));
  }
}

export async function getRules() {
  const { data } = await http.get(`/rules`);
  return data;
}

export async function fetchPois({ min_lon, min_lat, max_lon, max_lat, categories }) {
  try {
    const { data } = await http.get(`/pois`, {
      params: { min_lon, min_lat, max_lon, max_lat, categories: categories.join(",") },
    });
    return data.pois || [];
  } catch (e) {
    throw new Error(extractError(e));
  }
}

export async function fetchParking({ lat, lon, radius_km = 2, limit = 15, free_only = false, pmr_only = false }) {
  try {
    const { data } = await http.get(`/parking`, {
      params: { lat, lon, radius_km, limit, free_only, pmr_only },
    });
    return data; // { parkings, count, free_count, pmr_count }
  } catch (e) {
    throw new Error(extractError(e));
  }
}

export async function fetchChargingStations({ lat, lon, socket = "all", radius_km = 6, limit = 8, free_only = false, fast_only = false, open_247 = false }) {
  try {
    const params = { lat, lon, socket, radius_km, limit, free_only, fast_only, open_247 };
    const ocmKey = getOcmKey();
    if (ocmKey) params.ocm_key = ocmKey;
    if (getOcmPriority()) params.ocm_priority = true;
    const { data } = await http.get(`/charging-stations`, { params });
    return data; // { stations: [...], compatible_count }
  } catch (e) {
    throw new Error(extractError(e));
  }
}


export async function fetchForbiddenRoads() {
  try {
    const { data } = await http.get(`/forbidden-roads`);
    return data.roads || [];
  } catch (e) {
    return [];
  }
}

export async function createUserRoad(payload) {
  try {
    const { data } = await http.post(`/forbidden-roads/user`, payload);
    return data;
  } catch (e) {
    throw new Error(extractError(e));
  }
}

export async function deleteUserRoad(id) {
  const { data } = await http.delete(`/forbidden-roads/user/${id}`);
  return data;
}

export async function fetchVoices() {
  try {
    const { data } = await http.get(`/voices`);
    return data.voices || [];
  } catch (e) {
    return [];
  }
}

export async function listFavorites() {
  const { data } = await http.get(`/favorites`);
  return data.favorites || [];
}

export async function createFavorite(payload) {
  try {
    const { data } = await http.post(`/favorites`, payload);
    return data;
  } catch (e) {
    throw new Error(extractError(e));
  }
}

export async function updateFavorite(id, payload) {
  const { data } = await http.patch(`/favorites/${id}`, payload);
  return data;
}

export async function deleteFavorite(id) {
  const { data } = await http.delete(`/favorites/${id}`);
  return data;
}

// ---- TomTom real-time traffic ----
// Leaflet tile template served by our backend proxy (key stays server-side).
export const TRAFFIC_FLOW_URL = `${API}/traffic/flow/{z}/{x}/{y}.png?style=relative`;

export async function fetchTrafficIncidents({ min_lon, min_lat, max_lon, max_lat }) {
  try {
    const { data } = await http.get(`/traffic/incidents`, {
      params: { min_lon, min_lat, max_lon, max_lat },
    });
    return data.incidents || [];
  } catch (e) {
    return [];
  }
}

export async function fetchTrafficEta({ origin_lat, origin_lon, destination_lat, destination_lon }) {
  try {
    const { data } = await http.post(`/traffic/eta`, {
      origin_lat, origin_lon, destination_lat, destination_lon,
    });
    return data;
  } catch (e) {
    return null;
  }
}

// "Le Génie" local city anecdote for the given position.
export async function fetchLocalFact(lat, lon) {
  try {
    const { data } = await http.get(`/local-fact`, { params: { lat, lon } });
    return data;
  } catch (e) {
    return null;
  }
}

export async function fetchNearbyLandmark(lat, lon) {
  try {
    const { data } = await http.get(`/nearby-landmark`, { params: { lat, lon } });
    return data;
  } catch (e) {
    return null;
  }
}

export async function fetchRouteFlow(points) {
  try {
    const { data } = await http.post(`/traffic/route-flow`, { points });
    return data.points || [];
  } catch (e) {
    return [];
  }
}

export function formatDuration(min) {  if (min == null) return "—";
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  if (h > 0) return `${h} h ${String(m).padStart(2, "0")}`;
  return `${m} min`;
}

export function formatDistance(km) {
  if (km == null) return "—";
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(1)} km`;
}

// --- Genuine user ratings (for AggregateRating structured data) ---
export function getRatingClientId() {
  try {
    let id = localStorage.getItem("vsp_rating_cid");
    if (!id) {
      id = (window.crypto?.randomUUID?.() || String(Date.now()) + Math.random().toString(16).slice(2));
      localStorage.setItem("vsp_rating_cid", id);
    }
    return id;
  } catch (e) {
    return String(Date.now());
  }
}

export function getMyRating() {
  try {
    const v = parseInt(localStorage.getItem("vsp_my_rating"), 10);
    return Number.isFinite(v) ? v : 0;
  } catch (e) {
    return 0;
  }
}

export async function submitRating(stars) {
  const client_id = getRatingClientId();
  const { data } = await http.post(`/ratings`, { stars, client_id });
  try { localStorage.setItem("vsp_my_rating", String(stars)); } catch (e) { console.warn("Préférence note locale:", e); }
  return data; // { average, count }
}

export async function fetchRatingSummary() {
  try {
    const { data } = await http.get(`/ratings/summary`);
    return data;
  } catch (e) {
    return { average: 0, count: 0 };
  }
}
